import { FinSpaceServiceApi } from '../finspaceServiceApi';
import { StaticFilterValues, SEPARATOR_HYPHEN } from '../../constants/StaticFilterValues';
import { 
  generateAllDimensionsProductLineRequest,
  GetDimensionsReportingRequest,
  GetAllDimensionsReportingResponse
} from './transformers/request/ProductLineRequestTransformer';

/**
 * Constructs display name for ProductLine items
 * Format: fieldId - fieldName (e.g., "A1AJW - AWS&JWO")
 */
const constructFieldName = (fieldName: string, fieldId: string) => {
  return fieldId + SEPARATOR_HYPHEN + fieldName;
};

/**
 * Fetches filter data from the getAllDimensions API
 * Handles pagination and data transformation for ProductLine filters
 * 
 * @param filterType - Type of filter to fetch data for
 * @param params - Request parameters including hierarchy and filter values
 * @param handleAsyncError - Error handler function
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to transformed filter data
 */
export const fetchFilterData = async (
  filterType: string,
  params: any,
  handleAsyncError: (error: Error) => void,
  signal?: AbortSignal
) => {
  try {
    if (filterType === StaticFilterValues.PRODUCT_ID) {
      const transformedRequest = generateAllDimensionsProductLineRequest(params as GetDimensionsReportingRequest);

      const allData: GetAllDimensionsReportingResponse[] = [];
      let currentRequest = { ...transformedRequest };
      
      while (true) {
        const response = await FinSpaceServiceApi.getAllDimensions(currentRequest);

        allData.push(response);
        
        if (!response.lastEvaluatedKey) {
          break;
        }
        
        currentRequest = { 
          ...transformedRequest, 
          startKey: response.lastEvaluatedKey 
        };
      }

      const rawChildren = allData.flatMap(d => d.dimensionValues || []);

      const transformedChildren = rawChildren.map((item: any) => {
        // Exactly like FAEReportingWebsite - fieldId first, then separator, then fieldName
        const transformedItem = {
          value: item.fieldId,
          title: constructFieldName(item.fieldName, item.fieldId),
          key: item.fieldId,
          isLeaf: item.isLeaf,
          level: item.level, // Preserve level information from API response
          children: item.isLeaf ? undefined : []
        };
        
        return transformedItem;
      });

      return {
        children: transformedChildren
      };
    }
    
    throw new Error(`No API configured for filter type: ${filterType}`);
  } catch (error) {
    console.error(`Error fetching ${filterType} data:`, error);
    handleAsyncError(error as Error);
    throw error;
  }
};
